import { useCallback, useEffect, useMemo } from "react";
import { Badge } from "@stockline/ui/components/badge";
import { Button } from "@stockline/ui/components/button";
import { EditablePreOrderNumber } from "./ui/EditablePreOrderNumber";
import { PreOrderControls } from "./ui/PreOrderControls";
import { CommentPopover } from "./ui/CommentPopover";
import { ModifyControls } from "./ui/ModifyControls";
import { useModifyBufferOptional } from "../contexts/ModifyBufferContext";
import { usePreOrderLockOptional } from "../contexts/PreOrderLockContext";
import { PreOrderCategory, PreOrderType } from "@/app/api/types/preOrder";
import { useUpdatePreOrder } from "@/query/hooks/usePreOrders";
import { usePreOrderFeatureFlags } from "../hooks/usePreOrderFeatureFlags";
import { usePendingHeaderFields } from "../hooks/usePendingHeaderFields";
import { PendingFieldOverlay } from "./ui/PendingFieldOverlay";
import type { UUID } from "crypto";
import type { PreOrder } from "@/app/api/types/preOrder";
import { isProposalPreOrder } from "../types/proposal.types";
import { useProposalApprovalActions } from "@/app/ai/hooks/useProposalApprovalActions";
import { useProposalHeaderHandler } from "../hooks/useProposalHeaderHandler";
import { Bot, Check, Pencil, X, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  headerHasPendingAi,
  isCancelledPreOrder,
  isPreOrderModifiable,
} from "../services/recapService";
import { AiHeaderChangesBanner } from "./AiHeaderChangesBanner";
import {
  recordPendingHeaderWrite,
  clearPendingHeaderWrite,
} from "../services/pending-header-writes";
import { cn } from "@/lib/utils";
import { track } from "@/lib/analytics";
import { useRecapModeOptional } from "../contexts/RecapContext";
import { PreOrderSourceButton } from "./PreOrderSourceButton";

interface PreOrderHeaderProps {
  preOrder: PreOrder;
  /** True when the user has toggled edit-mode on a pending proposal. */
  isEditingProposal?: boolean;
  /** Provided when this surface allows toggling edit-mode on a proposal. */
  onToggleEditProposal?: () => void;
}

export function PreOrderHeader({
  preOrder,
  isEditingProposal = false,
  onToggleEditProposal,
}: Readonly<PreOrderHeaderProps>) {
  const { t } = useTranslation();
  const { hidePreOrderNumber, isVivoMode } = usePreOrderFeatureFlags();
  // Only PENDING / AI_PROPOSAL pre-orders accept header edits. Every other
  // status (CONFIRMED, EXPORTED, CANCELLED, …) renders the header read-only.
  const isModifiable = isPreOrderModifiable(preOrder);
  const isCancelled = isCancelledPreOrder(preOrder);
  const isDeleted = preOrder.deleted_at != null;
  const isProposal = isProposalPreOrder(preOrder);
  const modifyBuffer = useModifyBufferOptional();
  const lock = usePreOrderLockOptional();

  const updatePreOrderMutation = useUpdatePreOrder();
  const proposalHeader = useProposalHeaderHandler({ preOrder });
  // Per-field in-flight tracker. Header writes hit Dexie first and then wait
  // on SSE fan-out before the rendered value changes — without an
  // acknowledgement the cell looks frozen. Each cell wrapped in
  // PendingFieldOverlay surfaces the wait inline.
  const pendingFields = usePendingHeaderFields();
  // List-view header edits (this strip renders inside recap list / partner
  // cards) tag analytics with the workspace mode like the focus-view surfaces.
  const recapMode = useRecapModeOptional() ?? undefined;

  const dispatchHeaderUpdate = useCallback(
    (data: Record<string, unknown>) => {
      // Track every top-level key being written. `pre_order_metadata` updates
      // are tracked under the synthetic "comment" key so the wrapper around
      // CommentPopover (which owns the m3_comments editor) lights up too —
      // but skip expected-value tracking for that key since the prop the
      // spinner reads (`preOrder.comment`) is a string, not the metadata
      // blob, and the comparison would never match. Other fields map 1:1
      // onto the rendered prop so the spinner can hold until the cell value
      // catches up.
      const keys: string[] = [];
      const expectedValues: Record<string, unknown> = {};
      for (const rawKey of Object.keys(data)) {
        if (rawKey === "pre_order_metadata") {
          keys.push("comment");
          // No 1:1 prop comparison — fall back to settled-only behaviour.
        } else {
          keys.push(rawKey);
          expectedValues[rawKey] = data[rawKey];
        }
      }
      // Product analytics: one event per committed header field, fired only
      // once the write succeeds — never on a rejected edit. `field` is the raw
      // key name only, never the text. Iterate the mapped `keys` (not the raw
      // `data` keys) so an m3_comments save — dispatched as
      // `{ pre_order_metadata }` — reports `field: "comment"` like every other
      // comment edit, instead of splitting the engagement signal under
      // `pre_order_metadata`. `surface: recap_list` since this strip is the
      // list / partner-card header (the focus page swaps it for the
      // `po_detail` form, so the two never double-count).
      const emitHeaderChanged = () => {
        for (const field of keys) {
          track("pre_order_header_changed", {
            pre_order_id: preOrder.id,
            field,
            surface: "recap_list",
            mode: recapMode,
          });
        }
      };
      if (proposalHeader) {
        pendingFields.track(
          keys,
          (onSettled) => {
            // Proposal dispatch is awaitable but doesn't expose a TanStack
            // settled callback; chain manually so the indicator clears.
            Promise.resolve(proposalHeader.dispatch(data))
              .then(emitHeaderChanged)
              .finally(onSettled);
          },
          expectedValues,
        );
        return;
      }
      // Record the intended header value so an immediately-following confirm
      // can fold it into the export push-batch instead of racing this
      // change-queue write (STK-991). Kept in lockstep with the twin in
      // <PreOrderCommentBlock>.
      recordPendingHeaderWrite(preOrder.id, data);
      pendingFields.track(
        keys,
        (onSettled) => {
          updatePreOrderMutation.mutate(
            { id: preOrder.id as UUID, data },
            { onSuccess: emitHeaderChanged, onSettled },
          );
        },
        expectedValues,
      );
    },
    [
      proposalHeader,
      updatePreOrderMutation,
      preOrder.id,
      pendingFields,
      recapMode,
    ],
  );

  // Bound the module-level registry: drop this order's recorded-but-unconfirmed
  // header value when the header unmounts or the focus row changes, so a stale
  // value can't be folded into a later confirm (STK-991). Kept in lockstep with
  // the twin in <PreOrderCommentBlock>.
  useEffect(() => () => clearPendingHeaderWrite(preOrder.id), [preOrder.id]);

  const commentPopover = (
    <PendingFieldOverlay
      isPending={pendingFields.isPending("comment", preOrder.comment ?? "")}
      className="block w-full"
    >
      <CommentPopover
        value={preOrder.comment || ""}
        onChange={(value) => dispatchHeaderUpdate({ comment: value })}
        disabled={(!isModifiable && !isProposal) || lock.isLocked}
        isVivoMode={isVivoMode}
        flowMetadata={preOrder.pre_order_metadata}
        onM3CommentsChange={(m3Comments) =>
          dispatchHeaderUpdate({
            pre_order_metadata: {
              ...(preOrder.pre_order_metadata || {}),
              m3_comments: m3Comments,
            },
          })
        }
      />
    </PendingFieldOverlay>
  );

  let statusBadge: React.ReactNode = null;
  if (isDeleted) {
    statusBadge = (
      <Badge
        variant="outline"
        className="text-[10px] px-1.5 py-0 h-5 flex-shrink-0 border-red-400 text-red-500"
      >
        {t("ordersRecap.filters.deleted", "Deleted")}
      </Badge>
    );
  } else if (isCancelled) {
    statusBadge = (
      <Badge
        variant="outline"
        className="text-[10px] px-1.5 py-0 h-5 flex-shrink-0 border-orange-400 text-orange-500"
      >
        {t("ordersRecap.filters.cancelled")}
      </Badge>
    );
  }

  // Create proposal: replace entire header with proposal header
  if (isProposal && preOrder.__proposal.operation === "create") {
    return (
      <ProposalPreOrderHeader
        preOrder={preOrder}
        title={preOrder.__proposal.batch.title}
        batchId={preOrder.__proposal.batchId}
        isEditingProposal={isEditingProposal}
        onToggleEditProposal={onToggleEditProposal}
      />
    );
  }

  return (
    <div data-testid="preorder-header">
      {/* V3 AI header changes: pending `ai_suggestions[]` modify entry on the
       * pre-order header. Independent of the legacy `__proposal` path —
       * V3 stages into JSONB metadata, not the proposal_batch table. */}
      {headerHasPendingAi(preOrder) && (
        <AiHeaderChangesBanner preOrder={preOrder} />
      )}
      {/* Update/delete proposal: show approve/reject bar above normal header */}
      {isProposal && preOrder.__proposal.operation !== "create" && (
        <ProposalActionBar
          title={preOrder.__proposal.batch.title}
          batchId={preOrder.__proposal.batchId}
          proposals={preOrder.__proposal.proposals}
          isEditingProposal={isEditingProposal}
          onToggleEditProposal={onToggleEditProposal}
        />
      )}
      <div
        className={cn(
          "flex items-center gap-1.5 px-3 py-1 border-b border-gray-100 text-xs",
          isCancelled && "text-orange-400 opacity-60",
          isDeleted && "text-red-400 opacity-60",
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Left group: Source icon + Type badges + PO number */}
        {statusBadge}

        {/* Source icon: multi-source → split-cluster + popover; otherwise →
            single icon-only button. Shared with PreOrderFocusView so both
            surfaces resolve linked sources + pivot the chat panel through
            the same component. */}
        <PreOrderSourceButton preOrder={preOrder} />

        {/* Type badges */}
        {preOrder.type === PreOrderType.AVOIR && (
          <Badge
            variant="destructive"
            className="text-[10px] px-1.5 py-0 h-5 flex-shrink-0"
          >
            AVOIR
          </Badge>
        )}
        {preOrder.category === PreOrderCategory.PURCHASES && (
          <Badge
            data-testid="preorder-header-achat-badge"
            className="text-[10px] px-1.5 py-0 h-5 bg-purple-100 text-purple-700 flex-shrink-0"
          >
            ACHAT
          </Badge>
        )}

        {/* PO number */}
        {!hidePreOrderNumber && (
          <EditablePreOrderNumber
            preOrderId={preOrder.id}
            currentNumber={preOrder.pre_order_number}
            customerPoNumber={preOrder.customer_po_number}
            className="flex-shrink-0"
            // Lock-aware: read-only on any non-modifiable status (only
            // PENDING / AI_PROPOSAL stay editable) plus the per-PO hard
            // lock so re-exports, AI approvals, and failed-export states
            // all freeze the PO# field. Same pattern applied to
            // CommentPopover and PreOrderControls below.
            readOnly={!isModifiable || lock.isLocked}
          />
        )}

        {/* Comment fills middle space */}
        <div
          className="flex-1 min-w-0 hidden sm:block"
          data-testid="preorder-header-comment"
        >
          {commentPopover}
        </div>

        {/* Controls right-aligned */}
        <div className="hidden sm:flex items-center gap-1.5 flex-shrink-0">
          <PreOrderControls
            preOrder={preOrder}
            disabled={(!isModifiable && !isProposal) || lock.isLocked}
            onMutate={(data) => dispatchHeaderUpdate(data)}
            isFieldPending={pendingFields.isPending}
            commentProps={{ hidden: true }}
          />
          {modifyBuffer && (
            <ModifyControls preOrder={preOrder} buffer={modifyBuffer} />
          )}
        </div>
      </div>
    </div>
  );
}

function translateProposalTitle(
  title: string,
  t: (key: string) => string,
): string {
  if (title.startsWith("proposal.title.")) {
    const key = title.replace("proposal.title.", "");
    const translated = t(`aiProposals.proposalTitle.${key}`);
    if (translated !== `aiProposals.proposalTitle.${key}`) return translated;
  }
  return title;
}

function ProposalPreOrderHeader({
  preOrder,
  title,
  batchId,
  isEditingProposal,
  onToggleEditProposal,
}: Readonly<{
  preOrder: PreOrder;
  title: string;
  batchId: string;
  isEditingProposal?: boolean;
  onToggleEditProposal?: () => void;
}>) {
  const { t } = useTranslation();

  return (
    <div data-testid="preorder-header-proposal">
      <div
        className="flex items-center gap-1.5 px-3 py-1.5 border-b border-violet-200 text-xs"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="toolbar"
        tabIndex={-1}
      >
        {/* Bot icon */}
        <span className="h-7 w-7 flex-shrink-0 flex items-center justify-center rounded bg-violet-100 text-violet-600">
          <Bot className="h-3.5 w-3.5" />
        </span>

        {/* Title */}
        <span className="font-medium text-violet-700 truncate">
          {translateProposalTitle(title, t)}
        </span>

        {/* Comment if any */}
        {preOrder.comment && (
          <span className="text-muted-foreground truncate hidden sm:block">
            {preOrder.comment}
          </span>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Approve / Reject */}
        <ProposalActions
          batchId={batchId}
          isEditingProposal={isEditingProposal}
          onToggleEditProposal={onToggleEditProposal}
        />
      </div>
    </div>
  );
}

/** Compact approve/reject bar for update proposals — shown above the normal header */
function ProposalActionBar({
  title,
  batchId,
  proposals,
  isEditingProposal,
  onToggleEditProposal,
}: Readonly<{
  title: string;
  batchId: string;
  proposals: import("@stockline/shared/types/ai/proposal").Proposal[];
  isEditingProposal?: boolean;
  onToggleEditProposal?: () => void;
}>) {
  const { t } = useTranslation();

  // Extract display_fields from proposals
  type DisplayField = { label: string; value: string; oldValue?: string };
  const displayFields = useMemo<DisplayField[]>(() => {
    for (const p of proposals) {
      const fields = p.payload?.display_fields;
      if (Array.isArray(fields) && fields.length > 0) {
        return fields as DisplayField[];
      }
    }
    return [];
  }, [proposals]);

  return (
    <div
      className="bg-violet-50/50 border-b border-violet-200 text-xs"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      role="toolbar"
      tabIndex={-1}
      data-testid="proposal-action-bar"
    >
      <div className="flex items-center gap-1.5 px-3 py-1">
        <Bot className="h-3.5 w-3.5 text-violet-600 shrink-0" />
        <span className="font-medium text-violet-700 truncate">
          {translateProposalTitle(title, t)}
        </span>
        <div className="flex-1" />
        <ProposalActions
          batchId={batchId}
          isEditingProposal={isEditingProposal}
          onToggleEditProposal={onToggleEditProposal}
        />
      </div>
      {displayFields.length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-0.5 px-3 pb-1 pl-9">
          {displayFields.map((field) => (
            <span key={field.label} className="text-violet-700">
              <span className="text-violet-500">{field.label}:</span>{" "}
              {field.oldValue ? (
                <>
                  <span className="line-through text-violet-400">
                    {field.oldValue}
                  </span>{" "}
                  → <span className="font-medium">{field.value}</span>
                </>
              ) : (
                <span className="font-medium">{field.value}</span>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/** Shared approve/reject buttons used by both create and update proposal headers */
function ProposalActions({
  batchId,
  isEditingProposal,
  onToggleEditProposal,
}: Readonly<{
  batchId: string;
  isEditingProposal?: boolean;
  onToggleEditProposal?: () => void;
}>) {
  const { t } = useTranslation();
  const { acting, handleApprove, handleReject } =
    useProposalApprovalActions(batchId);

  return (
    <div className="flex items-center gap-1.5 flex-shrink-0">
      {onToggleEditProposal && (
        <Button
          size="sm"
          variant={isEditingProposal ? "default" : "outline"}
          onClick={onToggleEditProposal}
          disabled={acting}
          className="h-6 gap-1 text-xs px-2"
          data-testid="proposal-edit-toggle-button"
          aria-pressed={isEditingProposal}
        >
          <Pencil className="h-3 w-3" />
          {isEditingProposal ? t("aiProposals.editing") : t("aiProposals.edit")}
        </Button>
      )}
      <Button
        size="sm"
        variant="outline"
        onClick={handleReject}
        disabled={acting}
        className="h-6 gap-1 text-xs px-2"
        data-testid="proposal-reject-button"
        data-attr="proposal-reject-button"
      >
        <X className="h-3 w-3" />
        {t("aiProposals.reject")}
      </Button>
      <Button
        size="sm"
        onClick={handleApprove}
        disabled={acting}
        className="h-6 gap-1 text-xs px-2 bg-violet-600 hover:bg-violet-700"
        data-testid="proposal-approve-button"
        data-attr="proposal-approve-button"
      >
        {acting ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <Check className="h-3 w-3" />
        )}
        {t("aiProposals.approve")}
      </Button>
    </div>
  );
}
